// File: src/features/daily-planner/components/WeeklySummaryModal.tsx
import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Modal from '../../../components/common/Modal';
import Button from '../../../components/common/Button';
import Textarea from '../../../components/common/Textarea';
import Select from '../../../components/common/Select';
import { TrophyIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { WeeklySummary, WeeklySummaryCreatePayload, Category } from '../../../types';
import categoryService from '../../../services/categoryService';
import { toast } from 'react-hot-toast';

const summarySchema = z.object({
  win_text: z.string().min(5, 'Please describe your win in a bit more detail.'),
  challenge_text: z.string().min(5, 'Please describe your challenge in a bit more detail.'),
  win_category_id: z.preprocess(
    (arg) => (arg === "" || arg === undefined || arg === null || arg === "null" || arg === "0" ? null : parseInt(String(arg), 10)),
    z.number().int().positive().nullable().optional()
  ),
  challenge_category_id: z.preprocess(
    (arg) => (arg === "" || arg === undefined || arg === null || arg === "null" || arg === "0" ? null : parseInt(String(arg), 10)),
    z.number().int().positive().nullable().optional()
  ),
});

// MODIFIED: Defined separate types for raw input and resolved output
type SummaryFormResolvedOutputs = z.infer<typeof summarySchema>;
type SummaryFormRawInputs = z.input<typeof summarySchema>;

interface WeeklySummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: WeeklySummaryCreatePayload) => Promise<void>;
  isLoading: boolean;
  weekDisplayName: string;
  initialData?: WeeklySummary | null;
}

const WeeklySummaryModal: React.FC<WeeklySummaryModalProps> = ({ isOpen, onClose, onSubmit, isLoading, weekDisplayName, initialData }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setIsLoadingCategories(true);
      categoryService.getCategories({ include_archived: false })
        .then(setCategories)
        .catch(() => toast.error("Could not load categories."))
        .finally(() => setIsLoadingCategories(false));
    }
  }, [isOpen]);

  // MODIFIED: Updated useForm to use both Raw and Resolved types
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SummaryFormRawInputs, any, SummaryFormResolvedOutputs>({
    resolver: zodResolver(summarySchema),
    defaultValues: {
        win_text: '',
        challenge_text: '',
        win_category_id: null,
        challenge_category_id: null,
    }
  });

  useEffect(() => {
    if (initialData) {
      reset({
        win_text: initialData.win_text,
        challenge_text: initialData.challenge_text,
        win_category_id: initialData.win_category_id,
        challenge_category_id: initialData.challenge_category_id,
      });
    } else {
      reset({
        win_text: '',
        challenge_text: '',
        win_category_id: null,
        challenge_category_id: null,
      });
    }
  }, [initialData, reset]);

  // MODIFIED: Updated the SubmitHandler to use the resolved output type
  const handleFormSubmit: SubmitHandler<SummaryFormResolvedOutputs> = (data) => {
    onSubmit(data);
  };
  
  const modalTitle = (
    <div className="text-center">
        <h3 className="text-xl font-bold text-neutral-darkest">{initialData ? 'Edit Weekly Reflection' : 'Weekly Reflection'}</h3>
        <p className="text-sm text-neutral-dark mt-1">For the week of {weekDisplayName}</p>
    </div>
  );

  const categoryOptions = [
    { value: 'null', label: 'No Category' },
    ...categories.map(cat => ({ value: cat.id.toString(), label: cat.name }))
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="lg">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div>
          <label htmlFor="win_text" className="flex items-center text-lg font-semibold text-green-700 mb-2">
            <TrophyIcon className="h-6 w-6 mr-2" />
            What was your biggest win this week?
          </label>
          <Textarea id="win_text" {...register('win_text')} error={errors.win_text?.message} rows={4} placeholder="e.g., I successfully completed the big project at work..." />
          <Select
            label="Win Category (Optional)"
            options={categoryOptions}
            isLoading={isLoadingCategories}
            {...register('win_category_id')}
            wrapperClassName="mt-2"
          />
        </div>

        <div>
           <label htmlFor="challenge_text" className="flex items-center text-lg font-semibold text-red-700 mb-2">
            <ShieldExclamationIcon className="h-6 w-6 mr-2" />
            What was your biggest challenge?
          </label>
          <Textarea id="challenge_text" {...register('challenge_text')} error={errors.challenge_text?.message} rows={4} placeholder="e.g., I struggled with managing my time effectively and felt overwhelmed..." />
          <Select
            label="Challenge Category (Optional)"
            options={categoryOptions}
            isLoading={isLoadingCategories}
            {...register('challenge_category_id')}
            wrapperClassName="mt-2"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading || isLoadingCategories} disabled={isLoading || isLoadingCategories || (!!initialData && !isDirty)}>
            {initialData ? 'Update Reflection' : 'Submit Reflection'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default WeeklySummaryModal;